import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { authApiClient } from '../../../utils/axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { Button, Grid, Stack, Typography } from '@mui/material';
import CustomTextInput from '../../../components/CustomTextInput';
import { loginSuccess } from '../../../api/auth/authSlice';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import appConfig from '../../../config/appConfig';
import {
  applySelectedSession,
  persistAuthSession,
  resolvePostAuthRedirect,
  resolveSafePostAuthPath,
  selectOrganization,
} from '../../../utils/authToken';

const OTPVerificationForm = ({
  email,
  isLogin,
  orgMfa = false,
  organizationId,
  redirectTo: redirectToProp = '/dashboard',
}) => {
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(120);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const state = location.state || {};
  const resolvedOrgMfa = orgMfa || Boolean(state.orgMfa);
  const resolvedOrganizationId = organizationId || state.organizationId;
  const resolvedEmail = email || state.email;
  const redirectTo = resolveSafePostAuthPath(state.redirectTo || redirectToProp);
  const { login: l } = useTranslatedMessages();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    defaultValues: {
      email: resolvedEmail,
      otp: '',
    },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    authApiClient
      .post('/verify-mfa', data)
      .then((res) => {
        if (resolvedOrgMfa) {
          applySelectedSession(dispatch, queryClient);
          navigate(redirectTo, { replace: true });

          return;
        }

        if (!isLogin) {
          dispatch(
            showSnackbar({
              message: l.snackbar?.otp_verified_success ?? 'OTP successfully verified!',
              severity: 'success',
            }),
          );
          navigate('/login', { replace: true });

          return;
        }

        const { requiresOrgSelection } = persistAuthSession({
          requiresOrgSelection: !!res.data.requires_org_selection,
        });
        dispatch(loginSuccess({ requiresOrgSelection }));
        const nextPath = resolvePostAuthRedirect(requiresOrgSelection, redirectTo);
        navigate(nextPath, {
          replace: true,
          state: requiresOrgSelection ? { redirectTo } : undefined,
        });
      })
      .catch((err) => {
        if (err?.response?.data?.errors?.otp) {
          setError('otp', { message: err?.response?.data?.errors?.otp });
        } else {
          dispatch(
            showSnackbar({
              message: err?.response?.data?.errors?.message ?? l.snackbar?.error_verifying_otp ?? 'Error verifying OTP',
              severity: 'error',
            }),
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if ((isLogin || resolvedOrgMfa) && state.message) {
      dispatch(
        showSnackbar({
          message: state.message,
          severity: 'success',
        }),
      );
    }
  }, [dispatch, isLogin, resolvedOrgMfa, state.message]);

  useEffect(() => {
    if (timer > 0) {
      const countdown = setInterval(() => {
        setTimer((current) => current - 1);
      }, 1000);

      return () => clearInterval(countdown);
    }
  }, [timer]);

  const handleResendOtp = () => {
    const resendRequest = resolvedOrgMfa
      ? selectOrganization(authApiClient, resolvedOrganizationId)
      : authApiClient.post('/generate-otp-new-login', { email: resolvedEmail, isLogin });

    resendRequest
      .then((res) => {
        const message = resolvedOrgMfa
          ? res?.message || l.snackbar?.otp_sent_success || 'OTP sent successfully'
          : l.snackbar?.otp_sent_success || 'OTP sent successfully';

        dispatch(
          showSnackbar({
            message,
            severity: 'success',
          }),
        );
        setTimer(120);
      })
      .catch((err) => {
        dispatch(
          showSnackbar({
            message:
              err?.response?.data?.errors?.message ??
              l.snackbar?.error_sending_otp ??
              'Error sending OTP. Please try again.',
            severity: 'error',
          }),
        );
      });
  };

  return (
    <>
      <Stack sx={{ alignItems: 'center', mb: 4 }}>
        <img src={appConfig.brandLogoPath} alt="logo" width="50%" />
      </Stack>

      <Stack spacing={2}>
        <CustomTextInput
          name="email"
          label={l.form?.email_label ?? 'Email'}
          control={control}
          errors={errors}
          disabled={true}
        />
        <CustomTextInput name="otp" label={l.form?.otp_label ?? 'OTP'} control={control} errors={errors} />
        <Button type="button" variant="contained" className="submit" onClick={handleSubmit(onSubmit)}>
          {loading
            ? (l.actions?.verifying ?? 'Verifying...')
            : isLogin || resolvedOrgMfa
              ? (l.actions?.login ?? 'Login')
              : (l.actions?.verify_account ?? 'Verify Account')}
        </Button>
        <Grid container spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">
            <Link to="/login" style={{ textDecoration: 'none' }}>
              {l.links?.back_to_login ?? 'Back to Login'}
            </Link>
          </Typography>
          {timer > 0 ? (
            <Typography variant="body2" color="textSecondary" align="center">
              {(l.status?.resend_otp_in ?? 'Resend OTP in {minutes}:{seconds}')
                .replace('{minutes}', String(Math.floor(timer / 60)))
                .replace('{seconds}', String(timer % 60).padStart(2, '0'))}
            </Typography>
          ) : (
            <Button onClick={handleResendOtp} align="left" variant="text" color="primary">
              {l.actions?.resend_otp ?? 'Resend OTP'}
            </Button>
          )}
        </Grid>
      </Stack>
    </>
  );
};

export default OTPVerificationForm;
